import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PdfExportButton } from "@/components/PdfExportButton";
import { LongWeekendTable, FaqJsonLd, CrossLinks } from "@/components/pages/PseoExtras";
import { parseISO, calendarDatePath, calendarYearPath, dayOfWeek } from "@/lib/calendar/dates";
import {
  getHolidaysForYear,
  isEstimatedYear,
  SUPPORTED_YEARS,
  DISCLAIMER_SKB,
} from "@/data/holidays";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName, translateCategory } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";

export function LiburNasionalPage({ y, locale }: { y: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <Body y={y} />
    </SiteShell>
  );
}

function Body({ y }: { y: number }) {
  const { t, href, locale } = useLocale();
  const list = getHolidaysForYear(y).filter((h) => h.type === "libur");
  const allHolidays = getHolidaysForYear(y);
  const faq =
    locale === "id"
      ? [
          { q: `Berapa hari libur nasional ${y} di Indonesia?`, a: `Tahun ${y} memiliki ${list.length} hari libur nasional resmi berdasarkan SKB 3 Menteri.` },
          { q: `Kapan libur lebaran (Idul Fitri) ${y}?`, a: `Tanggal libur Idul Fitri ${y} dan cuti bersamanya tercantum lengkap di tabel pada halaman ini.` },
          { q: `Apakah hari Minggu termasuk libur nasional?`, a: `Hari Minggu adalah hari libur mingguan, bukan libur nasional. Tetap dihitung tanggal merah.` },
          { q: `Apa bedanya libur nasional dan cuti bersama?`, a: `Libur nasional ditetapkan UU dan SKB sebagai hari libur resmi. Cuti bersama adalah pengurang jatah cuti tahunan pegawai yang ditetapkan SKB 3 Menteri.` },
        ]
      : [
          { q: `How many public holidays in Indonesia in ${y}?`, a: `${y} has ${list.length} official public holidays based on the SKB 3 Menteri (Joint Ministerial Decree).` },
          { q: `When is Eid al-Fitr ${y}?`, a: `Eid al-Fitr ${y} dates and the related joint leave are listed in the table on this page.` },
          { q: `Is Sunday a public holiday?`, a: `Sunday is the weekly day off, not a public holiday, but still counts as a "red date".` },
          { q: `What is the difference between public holiday and joint leave?`, a: `Public holidays are official non-working days set by law. Joint leave (cuti bersama) reduces the employee's annual leave allowance and is set by joint ministerial decree.` },
        ];

  return (
    <>
      <FaqJsonLd items={faq} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link> /{" "}
        <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">{y}</Link> / {t.common.nationalHolidays}
      </nav>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.common.nationalHolidays} {y}</h1>
          <p className="mt-1 text-muted-foreground">
            {t.common.holidayCount(list.length)}
            {isEstimatedYear(y) && (
              <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs">{t.common.estimatedData}</span>
            )}
          </p>
        </div>
        <FeaturedSvg
          variant="holiday"
          title={`${t.common.nationalHolidays} ${y}`}
          subtitle={t.common.holidayCount(list.length)}
          badge={String(y)}
          locale={locale}
          className="hidden md:block md:w-72"
        />
        <PdfExportButton mode="year" year={y} />
      </header>

      <article className="prose prose-sm mb-6 max-w-none text-muted-foreground">
        <p>
          {locale === "id" ? (
            <>
              Daftar lengkap <strong>libur nasional {y}</strong> di Indonesia sesuai SKB 3 Menteri.
              Halaman ini menampilkan semua tanggal merah resmi dari Tahun Baru Masehi, Imlek, Nyepi,
              Wafat Isa Almasih, Idul Fitri, Hari Buruh, Waisak, Kenaikan Isa Almasih, Idul Adha,
              Hari Kemerdekaan, Tahun Baru Islam, Maulid Nabi, hingga Natal.
            </>
          ) : (
            <>
              Complete list of <strong>Indonesian public holidays {y}</strong> based on the Joint Ministerial Decree.
              This page lists every official red date, from New Year, Chinese New Year, Nyepi, Good Friday,
              Eid al-Fitr, Labour Day, Vesak, Ascension of Christ, Eid al-Adha, Independence Day,
              Islamic New Year, Mawlid, to Christmas.
            </>
          )}
        </p>
      </article>

      <HolidayTable items={list} year={y} />

      <div className="mt-6">
        <LongWeekendTable year={y} holidays={allHolidays} />
      </div>

      <p className="mt-6 rounded-md bg-muted p-3 text-xs text-muted-foreground">{DISCLAIMER_SKB}</p>
      <YearLinks current={y} prefix="libur-nasional" />
      <CrossLinks year={y} current="libur" />

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">{t.faq.title(`${t.common.nationalHolidays} ${y}`)}</h2>
        <div className="space-y-3">
          {faq.map((f) => (
            <div key={f.q} className="rounded-md border border-border bg-card p-3">
              <p className="text-sm font-semibold">{f.q}</p>
              <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function HolidayTable({
  items,
  year,
}: {
  items: { date: string; name: string; category: string }[];
  year: number;
}) {
  const { t, href, locale } = useLocale();
  if (items.length === 0) return <p className="text-muted-foreground">-</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2">{t.table.date}</th>
            <th className="px-3 py-2">{t.table.day}</th>
            <th className="px-3 py-2">{t.table.name}</th>
            <th className="px-3 py-2">{t.table.category}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((h) => {
            const p = parseISO(h.date)!;
            const dow = dayOfWeek(p.y, p.m, p.d);
            return (
              <tr key={`${h.date}-${h.name}`} className="border-t border-border">
                <td className="px-3 py-2">
                  <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="text-primary hover:underline">
                    {p.d} {t.common.months[p.m - 1]} {year}
                  </Link>
                </td>
                <td className="px-3 py-2">{t.common.days[dow]}</td>
                <td className="px-3 py-2 font-medium">{translateHolidayName(h.name, locale)}</td>
                <td className="px-3 py-2 capitalize text-muted-foreground">{translateCategory(h.category, locale)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function YearLinks({
  current,
  prefix,
}: {
  current: number;
  prefix: "libur-nasional" | "cuti-bersama";
}) {
  const { t, href } = useLocale();
  return (
    <div className="mt-6">
      <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{t.common.otherYears}</h2>
      <div className="flex flex-wrap gap-2">
        {SUPPORTED_YEARS.map((yr) => (
          <Link
            key={yr}
            to={href(`/${prefix}/${yr}`) as never}
            className={"rounded-md border border-border px-3 py-1 text-sm hover:bg-accent " + (yr === current ? "bg-accent font-semibold" : "")}
          >
            {yr}
          </Link>
        ))}
      </div>
    </div>
  );
}